import * as THREE from 'three';
import { VoxelGrid } from '../core/voxel-grid.js';
import { mulberry32 } from '../core/rng.js';

// ---------------------------------------------------------------------------
// Free-standing objects that need to be rotated to follow the river, so they
// get their own little grids and meshes instead of living in the world grid.
// ---------------------------------------------------------------------------

export function registerStructureBlocks(palette) {
    return {
        white: palette.add('bridgeWhite', 0xe8e5d8, { jitter: 0.035 }),
        deck: palette.add('bridgeDeck', 0x8c7045, { jitter: 0.09 }),
        iron: palette.add('bridgeIron', 0x4e4e53, { jitter: 0.05 }),
        log: palette.add('log', 0xa1855a, { jitter: 0.1 }),
        post: palette.add('mooringPost', 0x4a3b2a, { jitter: 0.08 }),
        lampOff: palette.add('signalOff', 0x2c3540, { emissive: 0.15, jitter: 0.03 }),
        lampOn: palette.add('signalOn', 0x63d69b, { emissive: 1.0, jitter: 0.02 }),
    };
}

function meshFrom(grid, palette, material, origin) {
    const geo = grid.build(palette, { voxelSize: origin.voxelSize, origin: origin.offset });
    return new THREE.Mesh(geo, material);
}

/**
 * An ophaalbrug — the white counterweighted drawbridge you cannot photograph
 * a Dutch canal without. Spans along local X; the deck sits at y = 0.
 */
export function createDrawbridge(palette, material, { voxelSize }) {
    const B = palette;
    const white = B.id('bridgeWhite');
    const deck = B.id('bridgeDeck');
    const iron = B.id('bridgeIron');

    const W = 24;
    const H = 18;
    const D = 8;
    const HINGE = 13;   // the leaf is hinged here and lifts towards the frame

    // Three grids, because three things move independently: the roadway and
    // its towers stay put, the leaf swings up, and the balance beam see-saws
    // the other way with the counterweight coming down.
    const g = new VoxelGrid(W, H, D);
    const leafGrid = new VoxelGrid(W, H, D);
    const beamGrid = new VoxelGrid(W, H, D);

    // --- the lifting leaf: the span between the near bank and the hinge ---
    leafGrid.box(2, 0, 2, HINGE, 0, 6, deck);
    leafGrid.box(2, 1, 2, HINGE, 1, 2, white);
    leafGrid.box(2, 1, 6, HINGE, 1, 6, white);
    for (let x = 3; x <= HINGE - 1; x += 4) {
        leafGrid.set(x, 2, 2, white);
        leafGrid.set(x, 2, 6, white);
    }
    // Hanger rods rise from the leaf to the beam, so they travel with it.
    for (const z of [3, 5]) {
        for (let y = 2; y <= 13; y++) leafGrid.set(6, y, z, iron);
    }

    // --- the fixed roadway beyond the hinge ---
    g.box(HINGE + 1, 0, 2, 21, 0, 6, deck);
    g.box(HINGE + 1, 1, 2, 21, 1, 2, white);
    g.box(HINGE + 1, 1, 6, 21, 1, 6, white);

    // Abutments at both banks.
    g.box(0, -1, 2, 2, 0, 6, iron);
    g.box(21, -1, 2, 23, 0, 6, iron);

    // The frame: two tall posts with a crossbeam.
    for (const z of [2, 6]) {
        g.box(15, 1, z, 15, 12, z, white);
    }
    g.box(15, 12, 2, 15, 12, 6, white);
    g.box(14, 13, 2, 16, 13, 6, white);

    // --- the balance beam, pivoting on top of the frame ---
    beamGrid.box(6, 14, 3, 20, 14, 3, white);
    beamGrid.box(6, 14, 5, 20, 14, 5, white);
    beamGrid.box(18, 12, 3, 20, 15, 5, iron);      // counterweight

    const origin = new THREE.Vector3(-(W / 2) * voxelSize, 0, -(D / 2) * voxelSize);
    const group = new THREE.Group();
    group.name = 'drawbridge';
    group.add(new THREE.Mesh(g.build(palette, { voxelSize, origin }), material));

    // Each moving part is built about its own pivot, then the pivot is put
    // back where the part belongs — otherwise it swings about the bridge's
    // centre instead of its hinge.
    const leafPivot = new THREE.Group();
    leafPivot.position.set((HINGE - W / 2) * voxelSize, 0, 0);
    leafPivot.add(new THREE.Mesh(
        leafGrid.build(palette, {
            voxelSize,
            origin: new THREE.Vector3(-HINGE * voxelSize, 0, -(D / 2) * voxelSize),
        }),
        material,
    ));
    group.add(leafPivot);

    const beamPivot = new THREE.Group();
    beamPivot.position.set((15 - W / 2) * voxelSize, 14 * voxelSize, 0);
    beamPivot.add(new THREE.Mesh(
        beamGrid.build(palette, {
            voxelSize,
            origin: new THREE.Vector3(-15 * voxelSize, -14 * voxelSize, -(D / 2) * voxelSize),
        }),
        material,
    ));
    group.add(beamPivot);

    let opened = 0;

    return {
        group,
        /** @param amount 0 closed, 1 fully raised */
        setOpen(amount, dt = 0.016) {
            opened += (amount - opened) * Math.min(1, dt * 1.6);
            // The leaf lies in -X from its hinge, so a POSITIVE rotation about
            // Z swings it down into the river. Both parts turn the same way:
            // the deck rises, and the counterweight on the far side of the
            // beam's pivot drops as it does.
            leafPivot.rotation.z = -opened * 1.15;
            beamPivot.rotation.z = -opened * 0.42;
        },
        get opened() {
            return opened;
        },
    };
}

/**
 * A duc d'Albe: the cluster of piles that marks a fairway. This pair stands
 * one on each side of the bridge and is what actually calls it up — a boat
 * crossing either one is standing in, whichever way she is heading.
 */
export function createFairwayPost(palette, material, { voxelSize, signal = true }) {
    const P = palette;
    const post = P.id('mooringPost');
    const H = 9;
    const g = new VoxelGrid(3, H + 2, 3);

    // Three piles lashed together, leaning slightly apart.
    g.box(1, 0, 1, 1, H, 1, post);
    g.box(0, 0, 1, 0, H - 3, 1, post);
    g.box(2, 0, 1, 2, H - 3, 1, post);
    g.box(0, H - 4, 1, 2, H - 4, 1, post);   // the lashing

    const body = meshFrom(g, palette, material, {
        voxelSize,
        offset: new THREE.Vector3(-1.5 * voxelSize, 0, -1.5 * voxelSize),
    });

    // The signal lamp on top gets its own mesh so it can be swapped between
    // the dark and the lit palette entry without rebuilding anything.
    const makeLamp = (id) => {
        const lg = new VoxelGrid(1, 1, 1);
        lg.set(0, 0, 0, id);
        const m = meshFrom(lg, palette, material, {
            voxelSize,
            offset: new THREE.Vector3(-0.5 * voxelSize, (H + 1) * voxelSize, -0.5 * voxelSize),
        });
        return m;
    };
    const group = new THREE.Group();
    group.add(body);
    group.name = 'fairwayPost';

    // Only the two bridge marks ever light. The shoal ring is a few dozen
    // posts that can never signal anything, so building them a lamp in each
    // state is two thirds of a draw call each, spent on a state that cannot
    // happen.
    if (!signal) {
        return { group, setLit() {} };
    }

    const off = makeLamp(P.id('signalOff'));
    const on = makeLamp(P.id('signalOn'));
    on.visible = false;
    group.add(off, on);

    return {
        group,
        setLit(lit) {
            off.visible = !lit;
            on.visible = lit;
        },
    };
}

/** A raft of sawlogs, moored beside the sawmill. */
export function createLogRaft(palette, material, { voxelSize, seed = 9 }) {
    const rng = mulberry32(seed);
    const log = palette.id('log');
    const W = 16;
    const D = 9;
    const g = new VoxelGrid(W, 3, D);

    for (let z = 1; z < D - 1; z += 2) {
        const len = W - 2 - Math.floor(rng() * 4);
        const x0 = 1 + Math.floor(rng() * 2);
        g.box(x0, 0, z, x0 + len - 1, 0, z, log);
        if (rng() < 0.35) g.box(x0 + 2, 1, z, x0 + len - 3, 1, z, log);
    }

    const mesh = meshFrom(g, palette, material, {
        voxelSize,
        offset: new THREE.Vector3(-(W / 2) * voxelSize, 0, -(D / 2) * voxelSize),
    });
    mesh.name = 'logs';
    return mesh;
}

/**
 * Place the river furniture: a drawbridge at mid-river and a log raft outside
 * the sawmill.
 */
const TRIP = 11;   // how close to a mark counts as standing in
const HOLD = 15;   // keep the leaf up until she is this far past the span

export function populateRiver(scene, polder, palette, material, { voxelSize, seed = 5 }) {
    const rng = mulberry32(seed);
    const worldSpan = polder.size * voxelSize;
    const toWorld = (gx, gz) => {
        const { x, z } = polder.toWorld(gx, gz);
        return new THREE.Vector3(x, 0, z);
    };

    const objects = [];

    // --- drawbridge, squared up with the river ---
    {
        const t = 0.63;   // between Het Lab and De Coder, clear of both yards
        const p = polder.canalPoint(t);
        const tan = polder.canalTangent(t);
        const bridge = createDrawbridge(palette, material, { voxelSize });
        const pos = toWorld(p.x, p.z);
        // Clear of the quay: the canal banks are raised ~1.6 voxels above the
        // surrounding land, so a deck at seaLevel + 2 sits inside the dike.
        pos.y = (polder.seaLevel + 3.9) * voxelSize;
        bridge.group.position.copy(pos);
        // Local +X should point across the river, i.e. along the normal.
        bridge.group.rotation.y = Math.atan2(-tan.x, -tan.z) + Math.PI;
        scene.add(bridge.group);

        // A marker on each approach, set back far enough that the leaf is
        // already lifting by the time a boat reaches the span.
        const gates = [t - 0.075, t + 0.075].map((gt) => {
            const gp = polder.canalPoint(gt);
            const gtan = polder.canalTangent(gt);
            // Just off the fairway, on the near bank, so nobody has to steer
            // round the thing they are being warned by.
            const off = 4.5;
            const post = createFairwayPost(palette, material, { voxelSize });
            const gpos = toWorld(gp.x + -gtan.z * off, gp.z + gtan.x * off);
            gpos.y = (polder.seaLevel - 1) * voxelSize;
            post.group.position.copy(gpos);
            scene.add(post.group);
            return { post, position: gpos.clone() };
        });

        objects.push({
            mesh: bridge.group, kind: 'bridge', bridge, position: pos.clone(), gates,
        });
    }

    // --- rafts of sawlogs, floating off the sawmill ---
    // A Zaan sawmill kept its timber in the water: logs came down the river
    // and lay there until they were cut. It is what the river looked like.
    for (const [t, off] of [[0.26, -6.5], [0.30, -5.0], [0.23, -4.2], [0.33, -7.2]]) {
        const raft = createLogRaft(palette, material, { voxelSize, seed: seed + t * 900 });
        const p = polder.canalPoint(t);
        const tan = polder.canalTangent(t);
        const pos = toWorld(p.x + -tan.z * off, p.z + tan.x * off);
        pos.y = polder.seaLevel * voxelSize - voxelSize * 0.4;
        raft.position.copy(pos);
        raft.rotation.y = Math.atan2(tan.x, tan.z) + Math.PI / 2 + (rng() - 0.5) * 0.4;
        scene.add(raft);
        objects.push({ mesh: raft, kind: 'raft', phase: rng() * 6.28 });
    }

    // --- and one more downriver ---
    {
        const t = 0.80;
        const raft = createLogRaft(palette, material, { voxelSize, seed: seed + 71 });
        const p = polder.canalPoint(t);
        const tan = polder.canalTangent(t);
        const pos = toWorld(p.x + -tan.z * -4.5, p.z + tan.x * -4.5);
        pos.y = polder.seaLevel * voxelSize - voxelSize * 0.4;
        raft.position.copy(pos);
        raft.rotation.y = Math.atan2(tan.x, tan.z) + Math.PI / 2;
        scene.add(raft);
        objects.push({ mesh: raft, kind: 'raft', phase: rng() * 6.28 });
    }

    const baseY = polder.seaLevel * voxelSize;

    const bridgeObj = objects.find((o) => o.kind === 'bridge');

    return {
        objects,
        /**
         * @param time    seconds, for the bobbing
         * @param dt      frame delta
         * @param boatAt  where the visitor's boat is, or null
         */
        update(time, dt = 0.016, boatAt = null) {
            if (bridgeObj) {
                // The bridgekeeper watches the two marks, not the bridge, so
                // a boat standing in from either direction calls the leaf up
                // before she gets there. Whichever mark trips, lights.
                let called = false;
                for (const gate of bridgeObj.gates) {
                    const lit = boatAt ? boatAt.distanceTo(gate.position) < TRIP : false;
                    gate.post.setLit(lit);
                    called = called || lit;
                }
                // Hysteresis: once she is committed, hold the span up until
                // she is clear of the bridge itself. Without it the leaf drops
                // between the two marks — on the boat's head, as she passes.
                const overBridge = boatAt
                    ? boatAt.distanceTo(bridgeObj.position) < HOLD
                    : false;
                bridgeObj.bridge.setOpen(called || overBridge ? 1 : 0, dt);
            }

            for (const o of objects) {
                if (o.kind === 'raft') {
                    o.mesh.position.y = baseY - voxelSize * 0.45
                        + Math.sin(time * 1.1 + o.phase) * 0.06;
                    o.mesh.rotation.z = Math.sin(time * 0.9 + o.phase) * 0.02;
                }
            }
        },
    };
}
